import { Timestamp } from 'firebase/firestore';
import type { EmailTypeId } from '../email/emailTypes';

// NEW: Define the structure for a single widget's configuration
export interface WidgetConfig {
  id: string;
  visible: boolean;
  order: number;
}

// NEW: Define the structure for PNG export styling
export interface ExportStyleSettings {
  id: string; // unitId
  multiUnitBackgroundMode?: 'mintleaf-default' | 'combined' | `unit:${string}`;
  // Row Coloring
  zebraStrength: number; // 0-100
  zebraColor: string;
  // Name Column Coloring
  nameColumnColor: string;
  // Header Coloring
  dayHeaderBgColor: string;
  categoryHeaderBgColor: string;
  // FIX: Added missing property to store calculated text color.
  categoryHeaderTextColor: string;
  // Grid and Border
  gridThickness: number; // 1-2
  gridColor: string;
  useRoundedCorners: boolean;
  borderRadius: number; // 6-12
  // Typography
  fontSizeCell: number; // 12-16
  fontSizeHeader: number; // 14-18
  // Layout
  useFullNameForDays: boolean;
}


export interface User {
  id: string;
  name: string;
  nickname?: string;
  nicknameLower?: string;
  lastName: string;
  firstName: string;
  fullName: string;
  email: string;
  role: 'Admin' | 'Unit Admin' | 'Unit Leader' | 'User' | 'Guest' | 'Demo User';
  unitIds?: string[];
  position?: string;
  dashboardConfig?: WidgetConfig[]; // NEW: Add dashboard configuration to user
  notifications?: {
    newSchedule?: boolean;
  };
  registrationEmailSent?: boolean;
  authUid?: string;
  accessProfileVersion?: number;
}

export interface Request {
  id: string;
  userId: string;
  userName: string;
  unitId?: string;
  dateKey?: string;
  dateKeys?: string[];
  startDateKey?: string;
  endDateKey?: string;
  startDate: Timestamp;
  endDate: Timestamp;
  note?: string;
  type: 'leave' | 'availability';
  timeRange?: { from: string; to: string };
  status: 'pending' | 'approved' | 'rejected' | 'withdrawn' | 'cancelled';
  revision?: number;
  createdAt: Timestamp;
  createdBy?: string;
  reviewedBy?: string;
  reviewedAt?: Timestamp;
  reviewNote?: string;
  resolvedAt?: Timestamp;
  resolvedBy?: string;
  resolution?: string;
}

export type GuestModificationRejectionPolicy = 'keep_original' | 'cancel_original';

export interface PendingGuestModification {
  id: string;
  status: 'pending' | 'approved' | 'rejected' | 'superseded' | 'cancelled';
  requestedAt?: Timestamp;
  requestedBy: 'guest';
  source: 'guestManageLink';
  rejectionPolicy: GuestModificationRejectionPolicy;
  original: {
    status: 'confirmed' | 'completed';
    headcount: number;
    startTime: Timestamp;
    endTime: Timestamp | null;
  };
  proposed: {
    headcount: number;
    startTime: Timestamp;
    endTime: Timestamp;
  };
  modifiedFields: string[];
  adminActionTokenHash?: string;
  adminActionExpiresAt?: Timestamp;
  adminActionUsedAt?: Timestamp | null;
  adminActionTokenVersion?: number;
  decidedAt?: Timestamp;
  decidedBy?: string | null;
  decision?: 'approved' | 'rejected';
  rejectionReason?: string | null;
}

export interface PendingGuestModificationSummary {
  id: string;
  status: 'pending';
  requestedAtMs: number | null;
  requestedBy: 'guest' | null;
  source: 'guestManageLink' | null;
  rejectionPolicy: GuestModificationRejectionPolicy | null;
  modifiedFields: string[];
  original: {
    status: string | null;
    headcount: number | null;
    startTimeMs: number | null;
    endTimeMs: number | null;
  };
  proposed: {
    headcount: number | null;
    startTimeMs: number | null;
    endTimeMs: number | null;
  };
}

export type ReservationKind = 'standard' | 'manual' | 'special_event';
export type ReservationBypassedConstraint = 'missing_email' | 'missing_phone' | 'booking_window' | 'opening_hours' | 'party_size' | 'capacity';

export interface Booking {
  id: string;
  unitId: string;
  name: string;
  headcount: number;
  occasion: string;
  source?: string;
  startTime: Timestamp;
  endTime: Timestamp;
  status: 'confirmed' | 'pending' | 'cancelled' | 'completed';
  createdAt: Timestamp;
  notes?: string;
  
  // For admin-created bookings
  phone?: string; 
  email?: string;

  // For guest-submitted bookings
  contact?: {
    phoneE164: string;
    email: string;
  };
  locale?: 'hu' | 'en';

  cancelledAt?: Timestamp;
  cancelReason?: string;
  referenceCode?: string;
  customData?: Record<string, string>;
  reservationMode?: 'auto' | 'request';
  reservationKind?: ReservationKind;
  override?: {
    enabled?: boolean;
    createdBy?: string | null;
    createdAt?: Timestamp;
    reason?: string | null;
    bypassedConstraints?: ReservationBypassedConstraint[];
  };
  emailPolicy?: { sendConfirmationEmail?: boolean; skippedReason?: string | null };
  adminActionTokenHash?: string;
  adminActionExpiresAt?: Timestamp;
  adminActionUsedAt?: Timestamp | null;
  adminActionHandledAt?: Timestamp;
  adminActionSource?: 'email' | 'manual';
  cancelledBy?: 'guest' | 'admin' | 'system';
  cancellationReasonCode?: string;
  manageTokenHash?: string;
  pendingGuestModification?: PendingGuestModification;
  hasPendingGuestModification?: boolean;
  pendingGuestModificationStatus?: PendingGuestModification['status'];
  pendingGuestModificationId?: string;
  pendingGuestModificationRequestedAt?: Timestamp;
  lastGuestModificationRequestedAt?: Timestamp;
  guestModificationCount?: number;
  guestModificationApprovedBy?: string;
  guestModifiedOnce?: boolean;
  guestModificationRevision?: number;
  zoneId?: string;
  assignedTableIds?: string[];
  seatingSource?: 'auto' | 'manual';
  manualTableMarker?: {
    label: string | null;
    revision: number;
    updatedAt?: Timestamp | null;
    updatedBy?: string | null;
    source?: 'manual_seating_module';
  };
  isVip?: boolean;
  attendanceStatus?: 'unknown' | 'attended' | 'no_show';
  attendedAt?: Timestamp | null;
  noShowAt?: Timestamp | null;
  attendanceMarkedAt?: Timestamp | null;
  attendanceMarkedBy?: string | null;
  attendanceSource?: string | null;
  attendanceNote?: string | null;
  preferredTimeSlot?: string | null;
  seatingPreference?: 'any' | 'bar' | 'table' | 'outdoor' | 'terrace';
  allocationIntent?: {
    timeSlot?: string | null;
    zoneId?: string | null;
    tableGroup?: string | null;
  };
  allocationDiagnostics?: {
    intentQuality?: 'none' | 'weak' | 'good';
    reasons?: string[];
    warnings?: string[];
    matchedZoneId?: string | null;
  };
  allocationOverride?: {
    enabled?: boolean;
    timeSlot?: string | null;
    zoneId?: string | null;
    tableGroup?: string | null;
    tableIds?: string[] | null;
    note?: string | null;
  };
  allocationOverrideSetAt?: Timestamp | null;
  allocationOverrideSetByUid?: string;
  allocationFinal?: {
    source?: 'intent' | 'override';
    timeSlot?: string | null;
    zoneId?: string | null;
    tableGroup?: string | null;
    tableIds?: string[] | null;
    locked?: boolean | null;
  };
  allocationFinalComputedAt?: Timestamp | null;
  allocated?: {
    zoneId?: string | null;
    tableIds?: string[];
    traceId?: string;
    decidedAtMs?: number;
    strategy?: string | null;
    diagnosticsSummary?: string;
    computedForStartTimeMs?: number;
    computedForEndTimeMs?: number;
    computedForHeadcount?: number;
    algoVersion?: string;
  };
}

export interface Zone {
  id: string;
  name: string;
  priority: number;
  isActive: boolean;
  isEmergency?: boolean;
  tags?: string[];
  type?: 'bar' | 'outdoor' | 'table' | 'other';
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

// --- Seating / Seat Layout (chairs) ---
export type SeatSide = 'north' | 'east' | 'south' | 'west' | 'radial';

export type SeatLayoutRect = {
  kind: 'rect';
  sides: {
    north?: number; // 0..3
    east?: number;  // 0..3
    south?: number; // 0..3
    west?: number;  // 0..3
  };
};

export type SeatLayoutCircle = {
  kind: 'circle';
  count: number; // 0..16
};

export type SeatLayout = SeatLayoutRect | SeatLayoutCircle;

export interface Table {
  id: string;
  name: string;
  zoneId: string;
  capacityMax: number;
  minCapacity: number;
  seatLayout?: SeatLayout;
  capacityTotal?: number;
  sideCapacities?: {
    north: number;
    east: number;
    south: number;
    west: number;
  };
  combinableWithIds?: string[];
  baseCombo?: {
    groupId: string;
    role: 'member' | 'aggregate';
    memberIds?: string[];
  };
  isActive: boolean;
  tableGroup?: string | null;
  tags?: string[];
  floorplanId?: string | null;
  shape?: 'rect' | 'circle' | string | null;
  w?: number | null;
  h?: number | null;
  radius?: number | null;
  snapToGrid?: boolean;
  locked?: boolean | null;
  x?: number | null;
  y?: number | null;
  rot?: number | null;
  canSeatSolo?: boolean;
  canCombine?: boolean | null;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

export interface Floorplan {
  id: string;
  name: string;
  width?: number | null;
  height?: number | null;
  isActive?: boolean;
  gridSize?: number;
  backgroundImageUrl?: string | null;
  obstacles?: FloorplanObstacle[];
  unitId?: string | null;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

export interface FloorplanObstacle {
  id: string;
  name?: string | null;
  x: number;
  y: number;
  w: number;
  h: number;
  rot?: number | null;
}

export interface TableCombination {
  id: string;
  tableIds: string[];
  isActive: boolean;
  /** Absolute top-left poses used only while this explicit combination is assembled. */
  targetTransforms?: Array<{
    tableId: string;
    x: number;
    y: number;
    rotation: number;
  }>;
  groupId?: string;
  resultingCapacity?: number;
  constraints?: string[];
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

export interface SeatingSettings {
  bufferMinutes?: number;
  defaultDurationMinutes?: number;
  allowGuestDurationEdit?: boolean;
  holdTableMinutesOnLate?: number;
  maxCombineCount?: number;
  vipEnabled?: boolean;
  activeFloorplanId?: string;
  soloAllowedTableIds?: string[];
  allocationEnabled?: boolean;
  allocationMode?: 'capacity' | 'floorplan' | 'hybrid';
  allocationStrategy?: 'bestFit' | 'minWaste' | 'priorityZoneFirst';
  defaultZoneId?: string;
  zonePriority?: string[];
  overflowZones?: string[];
  allowCrossZoneCombinations?: boolean;
  emergencyZones?: {
    enabled?: boolean;
    zoneIds?: string[];
    activeRule?: 'always' | 'byWeekday';
    weekdays?: number[];
  };
}

export interface PublicBookingDTO {
  id: string;
  unitId: string;
  unitName?: string;
  name: string;
  headcount: number;
  startTimeMs: number | null;
  endTimeMs: number | null;
  preferredTimeSlot?: string | null;
  seatingPreference?: 'any' | 'bar' | 'table' | 'outdoor' | 'terrace';
  status: 'confirmed' | 'pending' | 'cancelled' | 'completed';
  locale?: 'hu' | 'en';
  occasion?: string;
  source?: string;
  referenceCode?: string;
  cancelReason?: string;
  cancelledBy?: 'guest' | 'admin' | 'system';
  cancellationReasonCode?: string;
  contact?: {
    phoneE164?: string;
    email?: string;
  };
  pendingGuestModification?: PendingGuestModificationSummary | null;
  hasPendingGuestModification?: boolean;
  adminActionExpiresAtMs?: number | null;
  adminActionUsedAtMs?: number | null;
}

export interface ThemeSettings {
    primary: string;
    surface: string;
    background: string;
    textPrimary: string;
    textSecondary: string;
    accent: string;
    success: string;
    danger: string;
    radius: 'sm' | 'md' | 'lg' | 'xl';
    elevation: 'none' | 'low' | 'medium' | 'mid' | 'high';
    typographyScale: 'S' | 'M' | 'L';
    highlight?: string;
    backgroundImageUrl?: string;
    timeWindowLogoMode?: 'unit' | 'custom' | 'none';
    timeWindowLogoUrl?: string;
    headerBrandMode?: 'text' | 'logo';
    headerLogoMode?: 'unit' | 'custom' | 'none';
    headerLogoUrl?: string;
}

export interface CustomSelectField {
  id: string;
  label: string;
  labelEn?: string;
  options: string[];
  optionLabelsEn?: Record<string, string>;
}

export interface GuestFormSettings {
    customSelects?: CustomSelectField[];
    seatingPreferenceOptions?: Array<{
      value: 'any' | 'bar' | 'table' | 'terrace';
      label: string;
      labelEn?: string;
      enabled?: boolean;
    }>;
}

export type ServiceOpeningDay = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface ServiceOpeningSchedule {
    id: string;
    days: ServiceOpeningDay[];
    opensAt: string;
    closesAt: string;
}

export interface ServiceOpeningHour {
    id: string;
    name: string;
    nameEn?: string;
    schedules: ServiceOpeningSchedule[];
    isEnabled: boolean;
}

export interface ReservationCapabilitySettings {
    seatingEnabled?: boolean;
    allocationEnabled?: boolean;
    floorplanEnabled?: boolean;
}

export interface PublicReservationIdentity {
    displayName?: string | null;
    brandName?: string | null;
    source?: 'unit' | 'manual';
}

export interface ReservationSetting {
    id: string; // unitId
    /** Public compatibility projection of the unit canonical timezone. */
    timezone?: string;
    /** @deprecated Use timezone. */
    timeZone?: string;
    unitLocationRevision?: string;
    blackoutDates: string[]; // "YYYY-MM-DD"
    dailyCapacity?: number | null;
    capacityMode?: 'daily' | 'timeWindow';
    timeWindowCapacity?: number | null;
    bucketMinutes?: number;
    bufferMinutes?: number;
    upcomingWarningMinutes?: number;
    bookableWindow?: { from: string; to: string }; // "HH:mm"
    kitchenStartTime?: string | null;
    kitchenEndTime?: string | null;
    barStartTime?: string | null;
    barEndTime?: string | null;
    serviceOpeningHours?: ServiceOpeningHour[];
    guestForm?: GuestFormSettings;
    theme?: ThemeSettings;
    uiTheme?: 'minimal_glass' | 'elegant' | 'bubbly' | 'classic_elegant' | 'playful_bubble' | 'smooth_touch';
    schemaVersion?: number;
    reservationMode?: 'request' | 'auto';
    notificationEmails?: string[];
    publicBaseUrl?: string | null;
    reviewUrl?: string | null;
    reservationCapabilities?: ReservationCapabilitySettings;
    publicIdentity?: PublicReservationIdentity | null;
    /** @deprecated Use publicIdentity.displayName for public reservation branding. */
    publicName?: string | null;
    /** @deprecated Use publicIdentity.displayName for public reservation branding. */
    displayName?: string | null;
    /** @deprecated Use publicIdentity.brandName/publicIdentity.displayName for public reservation branding. */
    brandName?: string | null;
}

export interface ReservationCapacity {
  date: string;
  count?: number;
  totalCount?: number;
  byTimeSlot?: Record<string, number>;
  byTimeBucket?: Record<string, number>;
  byZone?: Record<string, number>;
  byTableGroup?: Record<string, number>;
  limit?: number;
  updatedAt?: Timestamp;
  capacityNeedsRecalc?: boolean;
  hasAllocationWarnings?: boolean;
}



export type ShiftTradeStatus = 'pending' | 'accepted' | 'rejected' | 'approved' | 'cancelled' | 'expired';

export interface ShiftTrade {
  id: string;
  unitId: string;
  shiftId: string;
  requesterUserId: string;
  targetUserId?: string | null;
  targetShiftId?: string | null;
  requestedTargetShiftId?: string | null;
  swapTargetShiftId?: string | null;
  acceptedByUserId?: string | null;
  status: ShiftTradeStatus;
  requesterMessage?: string;
  adminMessage?: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  expiresAt?: Timestamp;
  swapRevision?: number;
  swapAppliedAt?: Timestamp;
  swapAppliedBy?: string | null;
  swapEngineVersion?: string;
}

export interface Shift {
  id: string;
  userId: string;
  userName: string;
  unitId?: string;
  assignmentType?: 'standard' | 'shared_leadership';
  visibleUnitIds?: string[];
  divisionId?: string;
  position: string;
  start?: Timestamp | null;
  end?: Timestamp | null;
  note?: string;
  status: 'draft' | 'published';
  isDayOff?: boolean;
  isHighlighted?: boolean;
  dayKey?: string;
  version?: number;
  leaveRequestId?: string;
  source?: string;
  sourceShiftId?: string;
  generatedFrom?: string;
}

export interface TimeEntry {
  id: string;
  userId: string;
  unitId: string;
  startTime: Timestamp;
  endTime?: Timestamp;
  status: 'active' | 'completed';
}

export interface Todo {
  id: string;
  text: string;
  isDone: boolean;
  createdBy: string;
  createdByUid: string;
  createdAt: Timestamp;
  completedBy?: string;
  completedAt?: Timestamp;
  unitId?: string;
  seenBy?: string[];
  seenAt?: { [userId: string]: Timestamp };
  isDaily?: boolean;
  dailyType?: 'opening' | 'closing' | 'general';
  completedDate?: string;
}

export interface Feedback {
  id: string;
  text: string;
  unitId: string;
  createdAt: Timestamp;
  reactions?: {
    thankYou?: string[]; // Array of user IDs who reacted
  };
}

export interface Contact {
  id: string;
  name: string;
  phone: string;
  email?: string;
  note?: string;
  categoryId: string;
  isVisible: boolean;
  createdByUid?: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  unitId?: string;
}

export interface ContactCategory {
  id: string;
  name: string;
  isUserSelectable: boolean;
}

export interface Invitation {
  id: string;
  code: string;
  role: 'Admin' | 'Unit Admin' | 'User' | 'Guest';
  unitId: string;
  position: string;
  prefilledLastName?: string;
  prefilledFirstName?: string;
  status: 'active' | 'used';
  createdAt: Timestamp;
  usedBy?: string;
  usedAt?: Timestamp;
  mode?: 'create' | 'claim_existing';
  existingUserId?: string;
  email?: string;
  expiresAt?: Timestamp;
}

export interface FileMetadata {
  id: string;
  name: string;
  url: string;
  storagePath: string;
  size: number;
  contentType: string;
  uploadedBy: string;
  uploadedByUid: string;
  uploadedAt: Timestamp;
  unitId: string; // 'central' for shared documents
  categoryId?: string;
  subcategory?: string;
}

export interface KnowledgeCategory {
  id: string;
  title: string;
  order: number;
  unitId: string;
  subcategories?: string[];
}

export interface KnowledgeNote {
  id: string;
  title: string;
  content: string;
  categoryId: string;
  subcategory?: string;
  unitId: string;
  createdAt: Timestamp;
  createdBy: string;
  createdByUid: string;
}

// --- INVENTORY MODULE INTERFACES ---
export interface InventoryCategory {
  id: string;
  name: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface InventorySupplier {
  id: string;
  name: string;
  contactEmail?: string;
  phone?: string;
  note?: string;
  contactId?: string;
  leadTimeMinDays?: number;
  leadTimeMaxDays?: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface InventoryProduct {
  id: string;
  name: string;
  categoryId?: string;
  supplierId?: string; // legacy support
  supplierIds?: string[];
  avgDailyUsage?: number;
  unitPrice?: number;
  currency?: string;
  minOrderQuantity?: number;
  packageSize?: number;
  packageLabel?: string;
  safetyStock?: number;
  unitOfMeasure: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface InventoryIdealStock {
  id: string;
  productId: string;
  idealQuantity: number;
  updatedAt: Timestamp;
}

export interface InventoryCurrentStock {
  id: string;
  productId: string;
  currentQuantity: number;
  updatedAt: Timestamp;
  updatedByUserId?: string;
}

export interface InventorySettings {
  id?: string;
  targetDaysOfCover?: number;
  safetyDaysForSupplyRisk?: number;
}

export interface CanonicalUnitLocation {
    schemaVersion: 1;
    provider: 'geoapify' | 'open-meteo';
    providerPlaceId: string;
    name: string;
    displayName: string;
    countryCode: string;
    country?: string;
    administrativeArea?: string;
    postalCode?: string;
    latitude: number;
    longitude: number;
    timezone: string;
    updatedAt?: string;
    updatedByUserId?: string;
}

export interface Unit {
    id: string;
    name: string;
    unitTypeId?: 'hospitality-venue';
    contentTagIds?: string[];
    logoUrl?: string;
    logoFileId?: string;
    logo?: string;
    sheetId?: string;
    brandColors?: {
      primary: string; // Header
      secondary: string; // Buttons, Active Menu Item
      background: string; // App Background
    };
    uiTheme?: 'default' | 'brand';
    uiHeaderImageUrl?: string;
    uiBackgroundImageUrl?: string;
    canonicalLocation?: CanonicalUnitLocation;
    /** Compatibility projection for consumers not migrated to canonicalLocation yet. */
    timezone?: string;
    /** @deprecated Use canonicalLocation.timezone or timezone. */
    timeZone?: string;
}

export interface Position {
    id: string;
    name: string;
}

export interface DailySetting {
    isOpen: boolean;
    openingTime: string;
    closingTime: string;
    closingOffsetMinutes?: number;
    quotas: { [position: string]: number };
}

export interface ScheduleSettings {
    id: string; // Composite key: unitId_weekStartDate
    unitId: string;
    weekStartDate: string;
    showOpeningTime: boolean;
    showClosingTime: boolean;
    dailySettings: {
        [dayIndex: number]: DailySetting;
    };
}


export interface Permissions {
    canAddBookings: boolean;
    canManageSchedules: boolean;
    canManageUsers: boolean;
    canManagePositions: boolean;
    canGenerateInvites: boolean;
    canManageLeaveRequests: boolean;
    canSubmitLeaveRequests: boolean;
    canViewLeaveRequests: boolean;
    canViewTeamLeaveRequests: boolean;
    canCreateOwnLeaveRequests: boolean;
    canViewAllLeaveRequests: boolean;
    canReviewLeaveRequests: boolean;
    canAccessLeaveRequestAdminList: boolean;
    canManageTodos: boolean;
    canManageKnowledgeBase: boolean;
    canManageKnowledgeCategories: boolean;
    canManageContacts: boolean;
    canViewAllContacts: boolean;
    canManageUnits: boolean;
    canCreatePolls: boolean;
    canViewInventory: boolean;
    canManageInventory: boolean;
}

export type RolePermissions = {
    [role in User['role']]?: Partial<Permissions>;
};

// --- POLLS MODULE INTERFACES ---
export interface PollOption {
  id: string;
  label: string;
}

export interface Poll {
  id: string;
  question: string;
  options: PollOption[];
  unitId: string;
  multipleChoice: boolean;
  createdBy: string;
  createdAt: Timestamp;
  closesAt: Timestamp | null;
}

export interface PollWithResults extends Poll {
  results: Record<string, number>;
  totalVotes: number;
  userVote: string[] | null;
}

export interface PollVote {
  userId: string;
  selectedOptionIds: string[];
  votedAt: Timestamp;
}

// --- EMAIL SETTINGS INTERFACE ---
export interface EmailSettingsDocument {
  emailNotificationsEnabled?: boolean;
  enabledTypes: Record<string, boolean>;
  adminRecipients: Record<string, string[]>;
  templateOverrides?: {
    [key in EmailTypeId]?: {
      subject?: string;
      html?: string;
      subjectHu?: string;
      subjectEn?: string;
      bodyHu?: string;
      bodyEn?: string;
    }
  };
  adminDefaultEmail?: string;
}

// --- DEMO MODE DATA ---
export const demoUnit: Unit = { id: 'demo-unit-id', name: 'DEMO Üzlet', logoUrl: 'https://firebasestorage.googleapis.com/v0/b/mintleaf-74d27.appspot.com/o/unit_logos%2Fdemo-unit-id%2Flogo_demo.png?alt=media&token=1a2b3c4d-5e6f-7g8h-9i0j-1k2l3m4n5o6p' };
export const demoUser: User = {
    id: 'demo-user-id',
    name: 'demouser',
    lastName: 'Demo',
    firstName: 'Felhasználó',
    fullName: 'Demo Felhasználó',
    email: 'demo@example.com',
    role: 'Demo User',
    unitIds: [demoUnit.id],
    position: 'Munkatárs'
};
const otherDemoUser: User = {
    id: 'other-demo-user-id',
    name: 'teszteszter',
    lastName: 'Teszt',
    firstName: 'Eszter',
    fullName: 'Teszt Eszter',
    email: 'eszter@example.com',
    role: 'User',
    unitIds: [demoUnit.id],
    position: 'Pultos'
};

export const demoData = {
    requests: [
        { id: 'req1', userId: otherDemoUser.id, userName: otherDemoUser.fullName, status: 'approved', startDate: Timestamp.fromDate(new Date(new Date().setDate(new Date().getDate() + 8))), endDate: Timestamp.fromDate(new Date(new Date().setDate(new Date().getDate() + 9))), createdAt: Timestamp.now() },
    ] as Request[],
    bookings: [
        { 
            id: 'book1', 
            unitId: demoUnit.id,
            name: 'Nagy Család', 
            headcount: 5,
            occasion: 'Vacsora',
            startTime: Timestamp.fromDate(new Date(new Date().setHours(19, 0, 0, 0))),
            endTime: Timestamp.fromDate(new Date(new Date().setHours(21, 0, 0, 0))),
            status: 'confirmed',
            createdAt: Timestamp.now(),
            notes: 'Ablak melletti asztalt szeretnének.',
        },
    ] as Booking[],
    shifts: [
        { id: 'shift1', userId: demoUser.id, userName: demoUser.fullName, position: demoUser.position!, unitId: demoUnit.id, status: 'published', start: Timestamp.fromDate(new Date(new Date().setHours(8, 0, 0, 0))), end: Timestamp.fromDate(new Date(new Date().setHours(16, 0, 0, 0))) },
        { id: 'shift2', userId: otherDemoUser.id, userName: otherDemoUser.fullName, position: otherDemoUser.position!, unitId: demoUnit.id, status: 'published', start: Timestamp.fromDate(new Date(new Date().setHours(14, 0, 0, 0))), end: Timestamp.fromDate(new Date(new Date().setHours(22, 0, 0, 0))) }
    ] as Shift[],
    todos: [
        { id: 'todo1', text: 'Hűtők leltárazása', isDone: false, createdBy: 'Vezető', createdByUid: 'admin-id', createdAt: Timestamp.now(), unitId: demoUnit.id, seenBy: [demoUser.id] },
        { id: 'todo2', text: 'Szárazáru rendelés leadása', isDone: true, completedBy: otherDemoUser.fullName, completedAt: Timestamp.fromDate(new Date(Date.now() - 3600 * 1000)), createdBy: 'Vezető', createdByUid: 'admin-id', createdAt: Timestamp.fromDate(new Date(Date.now() - 24 * 3600 * 1000)), unitId: demoUnit.id, seenBy: [demoUser.id, otherDemoUser.id] }
    ] as Todo[],
    adminTodos: [] as Todo[],
    allUnits: [demoUnit] as Unit[],
    allUsers: [demoUser, otherDemoUser] as User[],
};
// --- END DEMO MODE DATA ---

export const mintLeafLogoSvgDataUri = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCI+PHBhdGggZmlsbD0iIzM0RDM5OSIgZD0iTTUgMjFjLjUtNC41IDIuNS04IDctMTBNOSAxOGM2LjIxOCAwIDEwLjUtMy4yODIgMTEtMTJ2LTJoLTQuMDE0Yy05IDAtMTEuOTg2IDQtMTIgOWMwIDEgMCAzIDIgNWgzeiIgLz48L3N2Zz4=";

